import {
  Alert,
  Button,
  Divider,
  Group,
  List,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useOrgScopedQuery } from "../../query/scope";
import { BindBotAccountModal } from "./BindBotAccountModal";
import {
  getRepoHostTokenGuide,
  type ProvisioningGuideResponse,
} from "./tokensApi";

export interface ProvisioningGuideModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly role: string;
}

// Тело модалки: загружает гайд и показывает шаги. Помимо ручной
// инструкции предлагает «использовать существующего бота» (Basic Auth) —
// после успешной привязки can-issue станет true и появится авто-выпуск.
function ProvisioningGuideModalBody(props: {
  readonly role: string;
}): ReactElement {
  const { t } = useTranslation();
  const { role } = props;

  const [bindOpen, setBindOpen] = useState(false);

  const guideQuery = useOrgScopedQuery<ProvisioningGuideResponse>({
    // Тот же ключ, что в `TokenUpdateModal`: обе модалки зовут
    // `getRepoHostTokenGuide(role)`, ответ у них общий.
    key: ["connections", "forgejo", "guide", role],
    queryFn: () => getRepoHostTokenGuide(role),
    retry: false,
  });

  if (guideQuery.isPending) {
    return <Loader />;
  }

  if (guideQuery.isError) {
    return <Alert color="red">{t("connections.loadingError")}</Alert>;
  }

  const data = guideQuery.data;
  const repoRestriction =
    data.repo_restriction.length > 0
      ? data.repo_restriction.join(", ")
      : t("connections.tokens.guide.repoRestrictionNone");

  return (
    <Stack gap="sm">
      <Text size="sm">
        <strong>{t("connections.tokens.guide.botUsername")}:</strong>{" "}
        {data.bot_username}
      </Text>
      <Text size="sm">
        <strong>{t("connections.tokens.guide.tokenName")}:</strong>{" "}
        {data.token_name}
      </Text>
      <Text size="sm">
        <strong>{t("connections.tokens.guide.requiredScopes")}:</strong>{" "}
        {data.required_scopes.join(", ")}
      </Text>
      <Text size="sm">
        <strong>{t("connections.tokens.guide.repoRestriction")}:</strong>{" "}
        {repoRestriction}
      </Text>
      <Text size="sm" fw={500}>
        {t("connections.tokens.guide.steps")}:
      </Text>
      <List type="ordered" size="sm">
        {data.steps.map((step) => (
          <List.Item key={step}>{step}</List.Item>
        ))}
      </List>

      <Divider />

      <Text size="sm">{t("connections.tokens.bindBot.guidePrompt")}</Text>
      <Group>
        <Button
          variant="default"
          onClick={() => {
            setBindOpen(true);
          }}
        >
          {t("connections.tokens.bindBot.openButton")}
        </Button>
      </Group>

      <BindBotAccountModal
        opened={bindOpen}
        onClose={() => {
          setBindOpen(false);
        }}
        role={role}
        onBound={() => {
          setBindOpen(false);
        }}
      />
    </Stack>
  );
}

// Модалка инструкции ручного заведения токена роли (GET /guide).
export function ProvisioningGuideModal(
  props: ProvisioningGuideModalProps,
): ReactElement {
  const { t } = useTranslation();
  const { opened, onClose, role } = props;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("connections.tokens.guide.title", { role })}
      size="lg"
    >
      {opened ? <ProvisioningGuideModalBody role={role} /> : null}
    </Modal>
  );
}
